import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import api from '../helpers/api';
import { getSingleCategory } from '../helpers/categories';
import IconSelect from '../components/IconSelect';

const CategoryEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [submitDisabled, setSubmitDisabled] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        icon: ''
    });

    useEffect(() => {
        if (id !== 'new') {
            fetchCategory();
        } else {
            setIsLoading(false);
        }
    }, [id]);

    const fetchCategory = async () => {
        try {
            const response = await api.get(`/categories/${id}`);
            const data = response.data.data;
            
            setFormData({
                name: data.name || '',
                icon: data.icon || ''
            });
        } catch (err) {
            console.error('Erreur lors du chargement de la catégorie:', err);
            setError('Erreur lors du chargement de la catégorie');
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleIconChange = (iconValue) => {
        setFormData(prev => ({
            ...prev,
            icon: iconValue
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitDisabled(true);

        try {
            if (id === 'new') {
                // Create new category
                await api.post('/categories', {
                    data: formData
                });
            } else {
                // Update existing category
                await api.put(`/categories/${id}`, {
                    data: formData
                });
            }

            navigate('/categories', {
                state: {
                    message: 'Catégorie ' + (id === 'new' ? 'créée' : 'modifiée') + ' avec succès'
                }
            });
        } catch (err) {
            console.error('Erreur lors de la sauvegarde:', err);
            setError('Erreur lors de la sauvegarde');
            setSubmitDisabled(false);
        }
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Chargement...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid px-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>
                    {id === 'new' ? 'Nouvelle catégorie' : 'Modifier la catégorie'}
                </h2>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/categories')}
                >
                    Retour à la liste
                </button>
            </div>

            {error && (
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            )}

            <div className="card shadow-sm">
                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        <div className="row mb-4">
                            <div className="col-md-8">
                                <div className="mb-3">
                                    <label className="form-label">Nom</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Icône</label>
                                    <IconSelect
                                        value={formData.icon}
                                        onChange={handleIconChange}
                                        className="w-100"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="d-flex justify-content-end gap-2">
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => navigate('/categories')}
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={submitDisabled}
                            >
                                {submitDisabled ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Enregistrement...
                                    </>
                                ) : (
                                    'Enregistrer'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CategoryEdit;